import { createSlice } from '@reduxjs/toolkit';
import { LeasingPremadeProduct } from 'modules/types/LeasingCalculator';
import {
  submitProductSelectionData,
  startLeasingCalculator,
  fetchPremadeProducts,
  requestFinancing,
} from './thunks';
import { LeasingOfferDetails } from '../../types/LeasingOffer';

export interface PremadeProductsState {
  content?: LeasingPremadeProduct[];
  totalPageCount?: number;
  loading?: boolean;
  error?: string;
}

const initialPremadeProductsState = {
  content: undefined,
  totalPageCount: 1,
  loading: true,
  error: undefined,
};

export interface LeasingCalculatorStepState<ResponseType> {
  result?: ResponseType;
  loading?: boolean;
  error?: string;
}

const initialStepState = {
  result: undefined,
  loading: false,
  error: undefined,
};

export interface LeasingCalculatorState {
  isAlive: LeasingCalculatorStepState<boolean>;
  premadeProducts?: PremadeProductsState;
  productSelectionData: LeasingCalculatorStepState<LeasingOfferDetails>;
  offerData: LeasingCalculatorStepState<boolean>;
}

const initialState: LeasingCalculatorState = {
  isAlive: initialStepState,
  premadeProducts: initialPremadeProductsState,
  productSelectionData: initialStepState,
  offerData: initialStepState,
};

export const leasingCalculatorSlice = createSlice({
  name: 'leasingCalculator',
  initialState,
  reducers: {
    resetState: (state) => {
      state.premadeProducts = initialPremadeProductsState;
      state.productSelectionData = initialStepState;
      state.offerData = initialStepState;
    },
    resetProductSelectionData: (state) => {
      state.productSelectionData = initialStepState;
    },
  },
  // TODO - leasing calculator extra reducers are very repetitive, consider refactoring
  extraReducers: (builder) => {
    // start calculator
    builder.addCase(startLeasingCalculator.pending, (state) => {
      state.isAlive.loading = true;
      state.isAlive.error = undefined;
      state.isAlive.result = undefined;
    });
    builder.addCase(startLeasingCalculator.fulfilled, (state, action) => {
      state.isAlive.loading = false;
      state.isAlive.result = action.payload;
    });
    builder.addCase(startLeasingCalculator.rejected, (state, action) => {
      state.isAlive.loading = false;
      state.isAlive.error = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    // premade products data
    builder.addCase(fetchPremadeProducts.pending, (state) => ({
      ...state,
      premadeProducts: {
        ...state.premadeProducts,
        loading: initialState.premadeProducts?.loading,
        error: initialState.premadeProducts?.error,
        content: initialState.premadeProducts?.content,
        totalPageCount: initialState.premadeProducts?.totalPageCount,
      },
    }));
    builder.addCase(fetchPremadeProducts.fulfilled, (state, action) => ({
      ...state,
      premadeProducts: {
        ...state.premadeProducts,
        loading: false,
        content: action.payload.premadeProducts,
        totalPageCount: action.payload.totalPageCount,
      },
    }));

    builder.addCase(fetchPremadeProducts.rejected, (state, action) => ({
      ...state,
      premadeProducts: {
        ...state.premadeProducts,
        loading: false,
        error: action.payload
          ? (action.payload as Error)?.message
          : action.error?.message,
      },
    }));

    // product selection data
    builder.addCase(submitProductSelectionData.pending, (state) => {
      state.productSelectionData.loading = true;
      state.productSelectionData.error = undefined;
      state.productSelectionData.result = undefined;
    });
    builder.addCase(submitProductSelectionData.fulfilled, (state, action) => {
      state.productSelectionData.loading = false;
      state.productSelectionData.result = action.payload;
    });
    builder.addCase(submitProductSelectionData.rejected, (state, action) => {
      state.productSelectionData.loading = false;
      state.productSelectionData.error = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });

    // offer data
    builder.addCase(requestFinancing.pending, (state) => {
      state.offerData.loading = true;
      state.offerData.error = undefined;
      state.offerData.result = undefined;
    });
    builder.addCase(requestFinancing.fulfilled, (state, action) => {
      state.offerData.loading = false;
      state.offerData.result = action.payload;
    });
    builder.addCase(requestFinancing.rejected, (state, action) => {
      state.offerData.loading = false;
      state.offerData.error = action.payload
        ? (action.payload as Error)?.message
        : action.error?.message;
    });
  },
});

export const { resetState, resetProductSelectionData } =
  leasingCalculatorSlice.actions;

export default leasingCalculatorSlice.reducer;
